<template>
  <div>
    <tax-return-delivery-dialog
      v-if="currentTaxReturn"
      :key="currentTaxReturn.id"
      :opened.sync="reviewTaxReturnDialogOpened"
      :currentCompanyId="currentCompany.id"
      :taxReturnId="currentTaxReturn.id"
      @refresh-table="refreshTable"
      @return-delivered="refreshTableUntilSent"
      @download-document="downloadDocument"
      @download-signed-pages-only="downloadSignedPagesOnly"
    />

    <send-tax-return-to-documents-dialog
      v-if="currentTaxReturn"
      :key="currentTaxReturn.id"
      :opened.sync="sendTaxReturnToDocumentsDialogOpened"
      :taxReturn="currentTaxReturn"
    />

    <audit-trail-dialog
      v-if="currentTaxReturn"
      :key="currentTaxReturn.id"
      :opened.sync="auditTrailDialogOpened"
      :taxReturn="currentTaxReturn"
    />

    <upload-tax-return-dialog
      :opened.sync="uploadTaxReturnDialogOpened"
      :currentCompany="currentCompany"
      :hideEntitySelect="true"
      :sectionId="currentSectionId"
      @refresh-table="refreshTableWithPolling"
    />

    <ImportTaxReturnDialog
      :opened.sync="importTaxReturnDialogOpened"
      :currentCompany="currentCompany"
      :hideEntitySelect="true"
      @refresh-table="refreshTable"
    />

    <SelectTaxReturnMethodDialog
      :opened.sync="selectTaxReturnMethodDialogOpened"
      :currentCompany="currentCompany"
      @open-upload-tax-return-dialog="uploadTaxReturnDialogOpened = true"
      @open-import-tax-return-dialog="importTaxReturnDialogOpened = true"
      @refresh-table="refreshTableWithPolling"
    />

    <v-card class="content-container mt-4">
      <!-- Show when the table is embeded in a questionnaire -->
      <v-card-title
        class="py-2 content-container__header"
        v-if="currentSectionId"
      >
        <v-layout align-center class="px-2">
          <span class="subheading font-weight-bold"> Deliver Tax Return </span>
          <v-spacer />

          <v-tooltip bottom>
            <v-btn
              color="primary"
              slot="activator"
              @click="uploadTaxReturnDialogOpened = true"
              class="default-input text-none"
              :disabled="currentQuestionnaire.locked || taxReturns.length > 0"
            >
              <v-icon class="mr-2">upload</v-icon>
              Upload Tax Return
            </v-btn>
            <span v-if="currentQuestionnaire.locked"
              >Please unlock the questionnaire</span
            >
            <span v-else-if="taxReturns.length > 0"
              >A tax return has been uploaded.</span
            >
            <span v-else
              >Uploading a tax return will start the delivery process</span
            >
          </v-tooltip>
        </v-layout>

        <soraban-action-button
          @click="refreshTable()"
          icon="refresh"
          help="Refresh Table"
          class="success--text"
        />
      </v-card-title>

      <!-- Show when the table is displayed in a standalone page like AdminDashboard -->
      <v-card-title v-else>
        <span class="subheading font-weight-bold">Tax Return Deliveries</span>
        <v-spacer />
        <span class="mr-2">Year</span>
        <div style="width: 100px" class="mr-2">
          <v-select
            v-model="currentYear"
            :items="yearOptionsWithAll"
            hide-details
            class="default-input body-1"
            solo
            small
          />
        </div>

        <soraban-action-button
          @click="refreshTable()"
          icon="refresh"
          help="Refresh Table"
          class="success--text"
        />
        <soraban-action-button
          v-if="importTaxReturnDraftEnabled"
          @click="selectTaxReturnMethodDialogOpened = true"
          icon="add"
          help="Send tax return delivery"
        />
        <soraban-action-button
          v-else
          @click="uploadTaxReturnDialogOpened = true"
          icon="upload"
          help="Upload tax return"
        />
      </v-card-title>

      <v-divider />

      <v-card-text>
        <div class="mb-4">
          <v-data-table
            :headers="headers"
            :items="taxReturnsMapped"
            class="tax-return-table text-xs-left"
            :pagination.sync="pagination"
            :rows-per-page-items="pagination.options"
            :loading="tableLoading"
            :class="
              $vuetify.breakpoint.mdAndUp
                ? 'overview-data-table'
                : 'mobile-overview-data-table'
            "
          >
            <template slot="items" slot-scope="props">
              <tr :key="props.index" slot="header">
                <td>
                  <v-layout
                    align-center
                    @click="viewTaxReturn(props.item)"
                    style="cursor: pointer"
                  >
                    <u class="ph-no-capture">{{ getName(props.item) }}</u>
                  </v-layout>
                </td>

                <td>
                  {{ props.item.tax_year }}
                </td>

                <td class="text-xs-left">
                  <v-layout align-center>
                    <i
                      class="material-icons-outlined mr-1"
                      :class="getTaxReturnStatus(props.item).class"
                      >{{ getTaxReturnStatus(props.item).icon }}</i
                    ><span class="font-weight-bold">{{
                      getTaxReturnStatus(props.item).text
                    }}</span>
                  </v-layout>
                </td>

                <td class="text-xs-left">
                  <v-tooltip bottom>
                    <v-layout slot="activator">
                      <sx-count-box
                        :count="props.item.completed_signature_count"
                        :total="props.item.total_signature_count"
                        :isDisabled="props.item.status == 'finished'"
                      />
                    </v-layout>

                    <v-flex class="text-xs-left">
                      <span v-if="props.item.total_signature_count === 0"
                        >No signatures requested.</span
                      >

                      <span
                        v-else-if="
                          props.item.total_signature_count ===
                            props.item.completed_signature_count
                        "
                        >All signatures completed</span
                      >

                      <span v-else-if="props.item.status === 'finished'"
                        >This item is marked as finished. Signatures are no
                        longer required.</span
                      >

                      <div v-else>
                        <b>
                          {{ props.item.remaining_signature_count }}
                          signature{{
                            props.item.remaining_signature_count === 1
                              ? 's'
                              : ''
                          }}
                          still needed:
                        </b>

                        <div v-for="form in props.item.e_sign_forms">
                          <div
                            v-if="
                              !form.wetSignatures ||
                                form.wetSignatures.length === 0
                            "
                            v-for="signature in form.signatures"
                          >
                            <div v-if="!signature.signatureData">
                              <span>
                                •
                                <span>
                                  <span
                                    v-if="
                                      form.form_id == 'other' &&
                                        !isEmpty(form.custom_form_name)
                                    "
                                    >{{ form.custom_form_name }}</span
                                  >
                                  <span v-else
                                    >{{ getFormNameById(form.form_id) }} (Form
                                    {{ form.form_id }})</span
                                  >
                                </span>
                                -
                                {{ signature.title }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-flex>
                  </v-tooltip>
                </td>

                <td class="text-xs-left">
                  <v-tooltip bottom>
                    <v-layout slot="activator">
                      <sx-count-box
                        :count="props.item.completed_payment_count"
                        :total="props.item.total_payment_count"
                      />
                    </v-layout>

                    <v-flex class="text-xs-left">
                      <span v-if="props.item.total_payment_count === 0"
                        >No tax payments requested.</span
                      >
                      <span
                        v-else-if="
                          props.item.total_payment_count ===
                            props.item.completed_payment_count
                        "
                        >All tax payments completed</span
                      >
                      <div v-else>
                        <b>
                          {{ props.item.remaining_payment_count }}
                          payment{{
                            props.item.remaining_payment_count === 1 ? 's' : ''
                          }}
                          still needed:
                        </b>

                        <div
                          v-if="
                            props.item.payment &&
                              props.item.payment.status !== 'paid'
                          "
                        >
                          • Tax Service Invoice - Due now
                        </div>

                        <div
                          v-for="voucher in props.item.sorted_payment_vouchers"
                        >
                          <div v-if="!voucher.is_paid">
                            <span>
                              •
                              <span
                                v-if="
                                  voucher.voucher_id === 'other' &&
                                    !isEmpty(voucher.custom_payment_name)
                                "
                              >
                                {{ voucher.custom_payment_name }}
                              </span>
                              <span v-else>{{
                                getVoucherNameById(voucher.voucher_id)
                              }}</span>
                              <span>
                                - Due date: {{ printDueDate(voucher) }}</span
                              >
                            </span>
                          </div>
                        </div>
                      </div>
                    </v-flex>
                  </v-tooltip>
                </td>

                <td class="text-xs-left">
                  <span>{{ moment(props.item.updated_at).fromNow() }}</span>
                </td>

                <td class="text-xs-left">
                  <span>{{
                    props.item.last_reminder_sent_at
                      ? moment(props.item.last_reminder_sent_at).fromNow()
                      : 'N/A'
                  }}</span>
                </td>

                <td class="text-xs-left">
                  <v-switch
                    :disabled="props.item.all_signed && props.item.all_paid"
                    :input-value="
                      props.item.all_signed && props.item.all_paid
                        ? false
                        : !props.item.reminder_disabled
                    "
                    @change="toggleReminder(props.item, $event)"
                    hide-details
                  ></v-switch>
                </td>

                <td>
                  <v-layout align-center justify-center>
                    <v-menu
                      transition="slide-y-transition"
                      bottom
                      left
                      nudge-bottom="30"
                      class="my-1"
                    >
                      <v-icon slot="activator" color="#0a1a25">
                        more_vert
                      </v-icon>

                      <v-list>
                        <v-list-tile @click="remove(props.item)">
                          <v-list-tile-title> Remove </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile
                          v-if="
                            props.item.status === 'sent' ||
                              props.item.status === 'finished'
                          "
                          @click="sendReminders(props.item)"
                        >
                          <v-list-tile-title>
                            Send reminder
                          </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile
                          v-if="
                            props.item.status === 'sent' ||
                              props.item.status === 'finished'
                          "
                          @click="viewAuditTrail(props.item)"
                        >
                          <v-list-tile-title>
                            View audit trail
                          </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile
                          v-if="
                            props.item.status === 'sent' ||
                              props.item.status === 'finished'
                          "
                          @click="
                            showSendTaxReturnToDocumentsDialog(props.item)
                          "
                        >
                          <v-list-tile-title>
                            Send tax return to Documents
                          </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile
                          v-if="props.item.status === 'sent'"
                          @click="markAsFinished(props.item)"
                        >
                          <v-list-tile-title>
                            Mark as finished
                          </v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </v-layout>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import UploadTaxReturnDialog from '@/components/returns/UploadTaxReturnDialog';
import ImportTaxReturnDialog from '@/components/returns/ImportTaxReturnDialog';
import TaxReturnDeliveryDialog from '@/components/returns/TaxReturnDeliveryDialog';
import SelectTaxReturnMethodDialog from '@/components/returns/SelectTaxReturnMethodDialog';
import AuditTrailDialog from '@/components/returns/AuditTrailDialog';
import API from '@/api';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import { yearOptions } from '@/constants/variables';
import TaxReturnsMixin from '@/mixins/TaxReturnsMixin';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import UtilsMixins from '@/mixins/UtilsMixins';
import axios from 'axios';
import SxCountBox from '@/components/sx/SxCountBox';
import SendTaxReturnToDocumentsDialog from '@/components/returns/SendTaxReturnToDocumentsDialog';

export default {
  components: {
    TaxReturnDeliveryDialog,
    UploadTaxReturnDialog,
    ImportTaxReturnDialog,
    SelectTaxReturnMethodDialog,
    AuditTrailDialog,
    SxCountBox,
    SendTaxReturnToDocumentsDialog,
  },

  mixins: [TaxReturnsMixin, FirmPreferenceMixins, UtilsMixins],

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    initialYear: {
      optional: true,
      type: String,
      default: false,
    },

    currentQuestionnaire: {
      type: Object,
      optional: true,
    },

    currentSectionId: {
      type: Number,
      optional: true,
    },
  },

  data() {
    return {
      currentYear: 'All',
      tableLoading: false,
      headers: [
        {
          text: 'Tax Return',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '25%',
        },
        {
          text: 'Tax Year',
          align: 'left',
          sortable: true,
          value: 'tax_year',
          width: '5%',
        },
        {
          text: 'Status',
          align: 'left',
          sortable: true,
          value: 'tax_return_status.value',
          width: '15%',
        },
        {
          text: 'Signatures',
          align: 'left',
          sortable: true,
          value: 'completed_signature_count',
          width: '10%',
        },
        {
          text: 'Payments',
          align: 'left',
          sortable: true,
          value: 'completed_payment_count',
          width: '10%',
        },
        {
          text: 'Last Activity',
          align: 'left',
          width: '12%',
          value: 'updated_at',
        },
        {
          text: 'Last Reminder Sent',
          align: 'left',
          width: '12%',
          value: 'last_reminder_sent_at',
        },
        {
          text: 'Reminder',
          align: 'left',
          sortable: true,
          width: '5%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '6%',
        },
      ],

      pagination: {
        descending: true,
        rowsPerPage: 25,
      },

      signatureStatus: 'All',

      reviewTaxReturnDialogOpened: false,
      uploadTaxReturnDialogOpened: false,

      taxReturns: [],
      currentTaxReturnId: null,
      selectTaxReturnMethodDialogOpened: false,
      importTaxReturnDialogOpened: false,

      auditTrailDialogOpened: false,

      sendTaxReturnToDocumentsDialogOpened: false,
    };
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
    }),

    yearOptionsWithAll() {
      return ['All', ...yearOptions.map(c => c.toString())];
    },

    currentTaxReturn() {
      return this.taxReturns.find(
        taxReturn => taxReturn.id === this.currentTaxReturnId,
      );
    },
  },

  methods: {
    moment,
    isEmpty,

    async refreshTable() {
      try {
        this.tableLoading = true;

        if (this.currentCompany && this.currentCompany.id) {
          const response = await this.getTaxReturnsByCompanyId();
          this.taxReturns = response.data;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.tableLoading = false;
      }
    },

    async refreshTableWithPolling(documentLocation) {
      let refreshInterval;

      const refreshTable = async () => {
        try {
          this.tableLoading = true;
          const response = await this.getTaxReturnsByCompanyId();
          this.taxReturns = response.data;

          if (
            this.taxReturns.find(
              request =>
                request.original_file === documentLocation &&
                request.status === 'action_required',
            )
          ) {
            clearInterval(refreshInterval);
            this.tableLoading = false;
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async refreshTableUntilSent(taxReturnId) {
      let refreshInterval;

      const refreshTable = async () => {
        try {
          this.tableLoading = true;
          const response = await this.getTaxReturnsByCompanyId();
          this.taxReturns = response.data;

          const taxReturn = this.taxReturns.find(req => req.id === taxReturnId);

          if (taxReturn && taxReturn.status === 'sent') {
            clearInterval(refreshInterval);
            this.tableLoading = false;
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    viewAuditTrail(item) {
      this.currentTaxReturnId = item.id;
      this.auditTrailDialogOpened = true;
    },

    async getTaxReturnsByCompanyId() {
      if (!this.currentCompany.id) Promise.reject();

      return axios.get('/tax_returns', {
        params: {
          company_id: this.currentCompany.id,
          year: this.currentYear === 'All' ? null : this.currentYear,
          section_id: this.currentSectionId,
        },
      });
    },

    showSendTaxReturnToDocumentsDialog(item) {
      this.sendTaxReturnToDocumentsDialogOpened = true;
      this.currentTaxReturnId = item.id;
    },
  },

  watch: {
    async initialYear() {
      this.currentYear = this.initialYear;
    },

    async currentYear() {
      await this.refreshTable();
    },

    reviewTaxReturnDialogOpened() {
      if (!this.reviewTaxReturnDialogOpened) {
        this.currentTaxReturnId = null;
      }
    },
  },

  async created() {
    await this.getFormDefinitions();
    await this.getVoucherDefinitions();
    await this.refreshTable();

    if (this.$route.query.taxReturnId) {
      const taxReturn = this.taxReturns.find(
        s => s.id === parseInt(this.$route.query.taxReturnId),
      );

      if (taxReturn) {
        this.viewTaxReturn(taxReturn);
      }
    }
  },
};
</script>

<style lang="postcss" scoped>
.content-container__header {
  background-color: #f7f7f7;
}
</style>
